import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { useFormBuilderStyles } from './FormBuilder.styles'
import { FormService } from '../../modules/Form/services/FormService'
import { FORM_SERVICE_KEY } from '../../modules/Form'
import { getFormContainer } from '../../container/form-module'
import { NewProduct } from '../../modules/Form/models/Form'

interface FormsTableProps {
  onEdit: (form: NewProduct) => void
  onCreateNew: () => void
  /** Fuerza refresco de la lista cuando cambia su valor */
  refreshKey?: number
}

export const FormsTable = ({ onEdit, onCreateNew, refreshKey }: FormsTableProps) => {
  const classes = useFormBuilderStyles()
  const formService = getFormContainer().get<FormService>(FORM_SERVICE_KEY)

  const [forms, setForms] = useState<NewProduct[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    formService.list().subscribe({
      next: (items) => {
        setForms(items)
        setLoading(false)
      },
      error: () => setLoading(false),
    })
  }, [refreshKey])

  return (
    <Box>
      {/* ── Cabecera ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#424242' }}>
          Formularios creados
        </Typography>
        <Button
          variant="contained"
          onClick={onCreateNew}
          className={classes.button}
        >
          + Nuevo formulario
        </Button>
      </Box>

      {/* ── Tabla ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : forms.length === 0 ? (
        <Typography
          variant="body2"
          className={classes.emptyState}
        >
          No hay formularios creados todavía. Crea el primero con el botón de arriba.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            borderColor: '#e8e8ea',
            borderWidth: '10px',
            borderStyle: 'solid',
            borderRadius: '10px',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: '#424242' }}>
                  Nombre del formulario
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#424242' }}>
                  Código (Nº Odoo)
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#424242' }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forms.map((form) => (
                <TableRow
                  key={form.id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell>{form.productName || '—'}</TableCell>
                  <TableCell>{form.odooNumber ?? '—'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      title="Editar formulario"
                      onClick={() => onEdit(form)}
                      sx={{
                        color: '#00b8e2',
                        '&:hover': { backgroundColor: 'rgba(0,184,226,0.1)' },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
